/**
 * Piezas de interfaz reutilizables: cargando, vacio, error, avisos y campos.
 */
import type { ReactElement, ReactNode } from 'react';
import type { Notice, NoticeKind } from '../hooks.ts';
import { Icon } from './Icon.tsx';
import type { IconName } from './Icon.tsx';

export function Spinner({ label }: { label?: string }): ReactElement {
  return (
    <span className="spinner" role="status" aria-live="polite">
      <span className="spinner__dot" />
      {label ? <span className="spinner__label">{label}</span> : null}
    </span>
  );
}

/** Placeholder de carga para listas. */
export function SkeletonRows({ rows = 5, className }: { rows?: number; className?: string }): ReactElement {
  const items = Array.from({ length: rows }, (_, index) => index);
  return (
    <div className={className ? `skeleton ${className}` : 'skeleton'} aria-hidden="true">
      {items.map((index) => (
        <div className="skeleton__row" key={index}>
          <div className="skeleton__avatar" />
          <div className="skeleton__lines">
            <div className="skeleton__line skeleton__line--short" />
            <div className="skeleton__line" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function SkeletonBlock({ height = 120 }: { height?: number }): ReactElement {
  return <div className="skeleton__block" style={{ height }} aria-hidden="true" />;
}

export function EmptyState({
  icon = 'info',
  title,
  description,
  action,
}: {
  icon?: IconName;
  title: string;
  description?: string;
  action?: ReactNode;
}): ReactElement {
  return (
    <div className="empty">
      <Icon name={icon} className="empty__icon" />
      <p className="empty__title">{title}</p>
      {description ? <p className="empty__description">{description}</p> : null}
      {action ? <div className="empty__action">{action}</div> : null}
    </div>
  );
}

export function ErrorBanner({
  message,
  onRetry,
  retryLabel = 'Reintentar',
}: {
  message: string;
  onRetry?: () => void;
  retryLabel?: string;
}): ReactElement {
  return (
    <div className="banner banner--error" role="alert">
      <Icon name="error" className="banner__icon" />
      <p className="banner__text">{message}</p>
      {onRetry ? (
        <button type="button" className="button button--ghost button--sm" onClick={onRetry}>
          <Icon name="refresh" />
          {retryLabel}
        </button>
      ) : null}
    </div>
  );
}

const NOTICE_ICONS: Record<NoticeKind, IconName> = {
  info: 'info',
  success: 'success',
  warning: 'warning',
  error: 'error',
};

export function NoticeStack({
  notices,
  onDismiss,
}: {
  notices: Notice[];
  onDismiss: (id: number) => void;
}): ReactElement | null {
  if (notices.length === 0) return null;
  return (
    <div className="notices" role="status" aria-live="polite">
      {notices.map((notice) => (
        <div className={`notice notice--${notice.kind}`} key={notice.id}>
          <Icon name={NOTICE_ICONS[notice.kind]} className="notice__icon" />
          <p className="notice__text">{notice.text}</p>
          <button
            type="button"
            className="notice__close"
            onClick={() => onDismiss(notice.id)}
            aria-label="Cerrar aviso"
          >
            <Icon name="close" />
          </button>
        </div>
      ))}
    </div>
  );
}

export function Field({
  label,
  htmlFor,
  hint,
  children,
  required,
}: {
  label: string;
  htmlFor?: string;
  hint?: string;
  children: ReactNode;
  required?: boolean;
}): ReactElement {
  return (
    <div className="field">
      <label className="field__label" htmlFor={htmlFor}>
        {label}
        {required ? <span className="field__required"> *</span> : null}
      </label>
      {children}
      {hint ? <p className="field__hint">{hint}</p> : null}
    </div>
  );
}

export function Chip({
  children,
  tone = 'neutral',
  title,
}: {
  children: ReactNode;
  tone?: 'neutral' | 'info' | 'success' | 'warning' | 'danger' | 'accent';
  title?: string;
}): ReactElement {
  return (
    <span className={`chip chip--${tone}`} title={title}>
      {children}
    </span>
  );
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
}: {
  options: { value: T; label: string; title?: string }[];
  value: T;
  onChange: (value: T) => void;
  ariaLabel: string;
}): ReactElement {
  return (
    <div className="segmented" role="group" aria-label={ariaLabel}>
      {options.map((option) => (
        <button
          type="button"
          key={option.value}
          className={option.value === value ? 'segmented__item is-active' : 'segmented__item'}
          aria-pressed={option.value === value}
          title={option.title}
          onClick={() => onChange(option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

export function StatCard({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}): ReactElement {
  return (
    <article className="stat">
      <p className="stat__label">{label}</p>
      <p className="stat__value">{value}</p>
      {hint ? <p className="stat__hint">{hint}</p> : null}
    </article>
  );
}
